/* eslint-disable max-lines */
import { view_profile as view_profile_helper } from "@/views/Feed/feed_helper.js";
import { get_token, get_user_details } from "@/router";
import {
  get_active_organization_id,
  normalize_organization_profile,
} from "@/utils/organization_profile";
import {
  get_organization_setup_items,
} from "@/utils/organization_setup_progress";
import HomeNavBar from "@/components/HomeNavBar/HomeNavBar.vue";
import CreatePortal from "@/components/CreatePortal/CreatePortal.vue";
import { build_home_journey_cards } from "./home_journey_cards";
import { HOME_FEATURE_ICONS } from "@/utils/home_feature_icons";
import homeJourneyStats from "./home_journey_stats";
import draggable from "vuedraggable";
import {
  FALLBACK_HOME_WIDGETS,
  homeJourneyLayoutComputed,
  homeJourneyLayoutMethods,
} from "./home_journey_layout";

export default {
  name: 'UserJourney',

  components: {
    draggable,
    HomeNavBar,
    CreatePortal,
  },

  inject: ['client_features'],

  props: {
    features: {
      type: Object,
      default: () => ({}),
    },

    userDetails: {
      type: Object,
      default: () => ({}),
    },
  },

  emits: [
    'create-post',
    'create-portal',
    'show-your-voice',
    'view-profile',
    'create-business-profile',
    'view-memberships',
  ],

  data: () => ({
    home_journey_stats: {},
    company: {},
    home_widget_definitions: FALLBACK_HOME_WIDGETS,
    home_widget_order: FALLBACK_HOME_WIDGETS.map(widget => widget.key),
    hidden_home_widgets: [],
    is_customizing_home: false,
    public_organizations: [],
    is_loading_public_organizations: false,
    has_membership_card: false,
  }),

  computed: {
    ...homeJourneyLayoutComputed,

    active_features() {
      return Object.keys(this.features || {}).length
        ? this.features
        : this.client_features;
    },

    active_user_details() {
      return Object.keys(this.userDetails || {}).length
        ? this.userDetails
        : get_user_details();
    },

    user_id() {
      return this.active_user_details?.user_id || this.active_user_details?.id || '';
    },

    token_exists() {
      return Boolean(get_token());
    },

    portals_label() {
      return String(this.active_features?.portals_label || 'Portals');
    },

    show_organisation_enabled() {
      return this.is_enabled_feature(this.active_features?.show_organisation);
    },

    organisation_name() {
      const details = this.active_user_details || {};
      const normalized_organization = normalize_organization_profile(this.company || {}, details);
      const organisation = details.organisation || details.organization || {};
      const active_organisation = details.active_organisation || details.active_organization || {};
      const candidates = [
        normalized_organization.company_name,
        active_organisation.organisation_name,
        active_organisation.organization_name,
        active_organisation.company_name,
        active_organisation.name,
        organisation.organisation_name,
        organisation.organization_name,
        organisation.company_name,
        organisation.name,
        details.organisation_name,
        details.organization_name,
        details.company_name,
        localStorage.getItem('company_name'),
      ];
      const name = candidates
        .map(value => String(value || '').trim())
        .find(Boolean);

      return name || '';
    },

    organization_setup_items() {
      return get_organization_setup_items(
        this.company || {},
        this.active_user_details || {}
      );
    },

    show_organisation_name() {
      return this.show_organisation_enabled && Boolean(this.organisation_name);
    },

    workspace_welcome_name() {
      return this.show_organisation_name ? `${this.organisation_name} ` : '';
    },

    home_dashboard_intro_text() {
      return this.show_organisation_enabled
        ? 'Your workspace is ready. Start with the essentials, then explore every tool your community can use to grow.'
        : 'Your workspace is ready. Start with the essentials, then explore the tools available to you.';
    },

    platform_launcher_intro_text() {
      return this.show_organisation_enabled
        ? 'Each tool works on its own. Together, they give your community one place to connect, participate and grow.'
        : 'Each tool works on its own. Together, they give you one place to create, manage, and stay up to date.';
    },

    marketplace_widget_title() {
      return this.show_organisation_enabled
        ? 'Everything your community needs, in one place.'
        : 'Everything you need, in one place.';
    },

    marketplace_widget_description() {
      return this.show_organisation_enabled
        ? 'Discover products, book trusted services, or grow a business with listings, orders and promo codes.'
        : 'Discover products, book trusted services, or manage your business listings, orders and promo codes.';
    },

    can_create_business_profile() {
      return this.is_enabled_feature(this.active_features?.show_business_profile)
        && !this.is_organization_admin_user;
    },

    journey_cards() {
      return build_home_journey_cards(this)
        .filter(card => this.is_enabled_feature(card.show))
        .map(card => ({
          ...card,
          stats: this.get_card_stats(card.statsKey),
        }));
    },

    home_feature_icons() {
      return HOME_FEATURE_ICONS;
    },
  },

  mounted() {
    this.load_home_widget_layout();
    this.fetch_home_journey_stats();
    this.fetch_company_profile();
    this.fetch_public_organizations();
    this.check_membership_card();
    window.addEventListener("focus", this.fetch_company_profile);
    window.addEventListener("organization-profile-updated", this.handle_organization_profile_updated);
  },

  beforeUnmount() {
    window.removeEventListener("focus", this.fetch_company_profile);
    window.removeEventListener("organization-profile-updated", this.handle_organization_profile_updated);
  },

  watch: {
    role_key() {
      this.load_home_widget_layout();
    },
    has_active_organization() {
      this.fetch_public_organizations();
    },
  },

  methods: {
    ...homeJourneyStats,
    ...homeJourneyLayoutMethods,

    is_enabled_feature(value) {
      return value === true || value === 'true' || value === 1 || value === '1';
    },

    fetch_public_organizations() {
      if (
        !this.token_exists ||
        !this.show_organisation_enabled ||
        !this.is_standard_user ||
        this.has_active_organization
      ) {
        this.public_organizations = [];
        return;
      }

      this.is_loading_public_organizations = true;
      $.ajax({
        url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/public_organizations/`,
        type: 'GET',
        success: response => {
          this.public_organizations = (response.organizations || []).slice(0, 3);
        },
        error: () => {
          this.public_organizations = [];
        },
        complete: () => {
          this.is_loading_public_organizations = false;
        },
      });
    },

    open_public_organization(organization) {
      this.$router.push({
        name: 'public_organization',
        params: { organization_id: organization.id },
      });
    },

    handle_card_click(card) {
      if (card.event) {
        this.handle_card_event(card.event);
      }
    },

    handle_card_event(event) {
      const actions = {
        'create-post': this.open_create_post,
        'create-portal': this.open_create_portal,
        'show-your-voice': this.open_your_voice,
        'view-profile': this.open_view_profile,
        'create-business-profile': this.open_create_business_profile,
        'view-memberships': this.go_to_view_membership,
      };
      const action = Reflect.get(actions, event);

      if (typeof action === 'function') {
        action();
      }

      this.$emit(event);
    },

    navigate_to(href) {
      this.$router.push(href);
    },

    go_to_view_membership() {
      $('#offcanvasExample').offcanvas('hide');

      if (this.has_membership_card) {
        return this.$router.push({
          name: 'manage_membership',
          params: { user_id: this.user_id },
        });
      }

      return this.$router.push('/view_membership');
    },

    check_membership_card() {
      const memberships_are_enabled = [
        this.active_features?.show_membership,
      ].some(this.is_enabled_feature);

      if (!this.token_exists || !memberships_are_enabled) {
        this.has_membership_card = false;
        return Promise.resolve();
      }

      return this.request_home_stat({
        url: `${process.env.VUE_APP_BACKEND_API_URL}/membership_management/get_membership_card/`,
        method: 'POST',
        headers: this.get_json_headers(),
      }).then((response) => {
        this.has_membership_card = Boolean(
          response?.status === 'success' && response?.data?.id
        );
      });
    },

    get_organization_id() {
      return this.active_user_details?.organization_id || get_active_organization_id();
    },

    handle_organization_profile_updated(event) {
      if (event?.detail) {
        this.company = event.detail;
        return;
      }

      this.fetch_company_profile();
    },

    fetch_company_profile() {
      const organization_id = this.get_organization_id();
      if (!organization_id || !get_token()) return;

      $.ajax({
        url: `${process.env.VUE_APP_BACKEND_API_URL}/company_management/get_company_profile/`,
        type: "POST",
        headers: {
          Authorization: `Token ${get_token()}`,
          "Content-Type": "application/json",
        },
        data: JSON.stringify({ organization_id }),
        success: (response) => {
          if (response?.status === "success") {
            this.company = response?.data?.company_data || {};
          }
        },
      });
    },

    open_create_post() {
      sessionStorage.setItem('open_create_feed_modal', 'true');
      this.$router.push('/feed');
    },

    open_events_widget_action() {
      if (!this.get_card_stats('events').length) {
        this.$router.push({ path: '/view_events', query: { create_event: 'true' } });
        return;
      }

      this.$router.push('/view_events');
    },

    open_your_voice() {
      sessionStorage.setItem('home_active_tab', 'top_loans_info');
      this.$router.push('/feed');
    },

    open_view_profile() {
      if (!get_token()) {
        this.$router.push('/login');
        return;
      }

      if (!this.user_id) {
        this.$router.push('/view_personal_profiles');
        return;
      }

      sessionStorage.setItem('open_profile_modal', 'true');
      sessionStorage.setItem('profile_target_user_id', this.user_id);

      view_profile_helper(this.$router, {
        id: this.user_id,
        user_id: this.user_id,
        first_name: this.active_user_details?.first_name,
        last_name: this.active_user_details?.last_name,
        profile_type: 'personal',
      });
    },

    open_create_business_profile() {
      if (!get_token()) {
        this.$router.push('/login');
        return;
      }

      this.$nextTick(() => {
        const modal = document.getElementById('company_profile_modal');
        if (!modal) return;

        if (window.bootstrap?.Modal) {
          window.bootstrap.Modal.getOrCreateInstance(modal).show();
          return;
        }

        $('#company_profile_modal').modal('show');
      });
    },

    open_create_portal() {
      this.$nextTick(() => {
        const modal = document.getElementById('portal_modal');
        if (!modal) return;

        if (window.bootstrap?.Modal) {
          window.bootstrap.Modal.getOrCreateInstance(modal).show();
          return;
        }

        $('#portal_modal').modal('show');
      });
    },
  },
};
